import { FC, useMemo } from "react";
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  useColorModeValue,
  Link,
  BoxProps,
  FlexProps,
  Center,
} from "@chakra-ui/react";
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from "react-router-dom";
import { FiHome, FiSettings, FiUsers } from "react-icons/fi";
import { GiAbstract029 } from "react-icons/gi";
import { BiCctv, BiKey } from "react-icons/bi";
import { MdDashboard } from "react-icons/md";
import { IconType } from "react-icons";
import { ReactText } from "react";
import Logo from "components/Logo";
import { useAuthContext } from "./AuthProvider";

interface LinkItemProps {
  name: string;
  path: string;
  icon: IconType;
}
const LinkItems: Array<LinkItemProps> = [
  { name: "Dashboard", icon: MdDashboard, path: "/" },
  { name: "Rumah", icon: FiHome, path: "/houses" },
  { name: "Users", icon: FiUsers, path: "/users" },
  { name: "Admin", icon: BiKey, path: "/admins" },
  { name: "CCTV Kawasan", icon: BiCctv, path: "/cctv" },
  { name: "Gerbang Masuk", icon: GiAbstract029, path: "/gate" },
  { name: "Pengaturan", icon: FiSettings, path: "/settings" },
];

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const Sidebar: FC<SidebarProps> = ({ onClose, ...rest }) => {
  const { user } = useAuthContext();
  return (
    <Box
      transition="3s ease"
      bg={useColorModeValue("white", "gray.900")}
      borderRight="1px"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex h="28" alignItems="center" mx="8" justifyContent="space-between">
        <Center flex="1" background="white" p="2">
          <Logo w="50%" />
        </Center>
        <CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} />
      </Flex>
      {LinkItems.filter(
        (link) => !(user?.role === "admin" && link.path === "/admins")
      ).map((link) => (
        <NavItem to={link.path} key={link.name} icon={link.icon}>
          {link.name}
        </NavItem>
      ))}
    </Box>
  );
};

interface NavItemProps extends FlexProps {
  icon: IconType;
  children: ReactText;
  to: string;
}
const NavItem = ({ to, icon, children, ...rest }: NavItemProps) => {
  let resolved = useResolvedPath(to);
  let match = useMatch({ path: resolved.pathname, end: false });
  const location = useLocation();
  const bg = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.800", "white");

  const isActive = useMemo(() => {
    return location.pathname === "/" ? match : match && to !== "/";
  }, [match, location, to]);

  return (
    <Link
      as={RouterLink}
      to={to}
      href="#"
      style={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
    >
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        bg={isActive ? "brand.400" : bg}
        color={isActive ? "white" : textColor}
        _hover={{
          bg: "brand.400",
          color: "white",
        }}
        {...rest}
      >
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            _groupHover={{
              color: "white",
            }}
            as={icon}
          />
        )}
        {children}
      </Flex>
    </Link>
  );
};

export default Sidebar;
